import { createContext, useContext, useState, type ReactNode } from 'react'
import { create, useStore, type StoreApi, type UseBoundStore } from 'zustand'
import { type CalendarDropForbiddenReason } from './dropProposal'

/** What a day cell shows while something is held over it. */
export type CalendarDayDropState = 'allowed' | CalendarDropForbiddenReason

export type CalendarDragState = {
  draggedItemId: string | null
  hoveredDayKey: string | null
  forbiddenReason: CalendarDropForbiddenReason | null
  onDragStart: (itemId: string) => void
  onDragEnter: (
    dayKey: string,
    forbiddenReason: CalendarDropForbiddenReason | null
  ) => void
  onDragLeave: (dayKey: string) => void
  reset: () => void
}

type CalendarDragStore = UseBoundStore<StoreApi<CalendarDragState>>

const CalendarDragContext = createContext<CalendarDragStore | undefined>(
  undefined
)
CalendarDragContext.displayName = 'CalendarDragContext'

/**
 * Holds what is being dragged and which day it is over.
 *
 * A pointer moving across the grid changes the hovered day many times a second.
 * The state lives in a store rather than in React state so only the two cells
 * whose own answer changed are drawn again, instead of every day in the month.
 */
export const CalendarDragProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [store] = useState<CalendarDragStore>(() =>
    create<CalendarDragState>()((set) => ({
      draggedItemId: null,
      forbiddenReason: null,
      hoveredDayKey: null,

      onDragStart: (itemId) => set(() => ({ draggedItemId: itemId })),

      onDragEnter: (dayKey, forbiddenReason) =>
        set(() => ({ forbiddenReason, hoveredDayKey: dayKey })),

      onDragLeave: (dayKey) =>
        set((state) =>
          state.hoveredDayKey === dayKey
            ? { forbiddenReason: null, hoveredDayKey: null }
            : state
        ),

      reset: () =>
        set(() => ({
          draggedItemId: null,
          forbiddenReason: null,
          hoveredDayKey: null,
        })),
    }))
  )

  return (
    <CalendarDragContext.Provider value={store}>
      {children}
    </CalendarDragContext.Provider>
  )
}

const useCalendarDragStore = (): CalendarDragStore => {
  const store = useContext(CalendarDragContext)
  if (store === undefined) {
    throw new Error(
      'Calendar drag hooks must be used inside a CalendarDragProvider.'
    )
  }
  return store
}

/** Reads one thing out of the drag state. */
export const useCalendarDragState = <T,>(
  selector: (state: CalendarDragState) => T
): T => useStore(useCalendarDragStore(), selector)

/** Whether this day is the one being dragged over, and whether it will take it. */
export const useDayDropState = (dayKey: string): CalendarDayDropState | null =>
  useCalendarDragState((state) =>
    state.hoveredDayKey === dayKey ? (state.forbiddenReason ?? 'allowed') : null
  )
